import {
  Alert,
  AutoComplete,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  notification,
} from 'antd';
import { CopyOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import TabbedPage from 'components/tabbed-page';
import { useCurrentRoles } from 'hooks/use-current-roles';
import { CompanyInfo, formatVnd, QUOTE_STATUSES, QuoteSettings } from './calc';
import {
  EquipmentItem,
  equipmentApi,
  errorText,
  imageUrl,
  PermissionRow,
  QuoteView,
  STATUS_COLOR,
  STATUS_LABEL,
} from './api';
import CustomizedUpload from 'components/customized-upload';
import { UPLOAD_TYPE } from 'constants/common';
import NoAccess from './components/no-access';
import styles from './style.module.scss';

const dateText = (iso: string) => (iso ? new Date(iso).toLocaleDateString('vi-VN') : '');

// ---------- Tab Báo giá ----------

const QuotesTab = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<QuoteView[] | null>(null);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<string | undefined>();

  const load = useCallback(() => {
    equipmentApi
      .quotes({ q: q || undefined, status })
      .then(setRows)
      .catch((e) => {
        setRows([]);
        notification.error({ message: 'Không tải được báo giá', description: errorText(e) });
      });
  }, [q, status]);
  useEffect(load, [load]);

  const duplicate = (id: number) =>
    equipmentApi
      .duplicate(id)
      .then((r) => navigate(`/equipment-quotes/${r.quote.id}`))
      .catch((e) =>
        notification.error({ message: 'Chưa nhân bản được', description: errorText(e) })
      );

  const remove = (id: number) =>
    equipmentApi
      .removeQuote(id)
      .then(load)
      .catch((e) => notification.error({ message: 'Chưa xoá được', description: errorText(e) }));

  return (
    <div>
      <div className={styles.toolbar}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/equipment-quotes/new')}
        >
          Tạo báo giá
        </Button>
        <Input.Search
          allowClear
          placeholder="Tìm mã, tên Creator, SĐT, TikTok ID"
          style={{ width: 320, maxWidth: '100%' }}
          onSearch={(v) => setQ(v.trim())}
        />
        <Select
          allowClear
          placeholder="Trạng thái"
          style={{ width: 180 }}
          value={status}
          onChange={setStatus}
          options={QUOTE_STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s] }))}
        />
      </div>
      <Table
        rowKey="id"
        loading={!rows}
        dataSource={rows ?? []}
        pagination={{ defaultPageSize: 20 }}
        scroll={{ x: 960 }}
        onRow={(r) => ({ onDoubleClick: () => navigate(`/equipment-quotes/${r.id}`) })}
        columns={[
          {
            title: 'Mã',
            dataIndex: 'code',
            width: 130,
            render: (v, r) => <Link to={`/equipment-quotes/${r.id}`}>{v}</Link>,
          },
          {
            title: 'Creator',
            render: (_, r) => (
              <div>
                <b>{r.customerName || '—'}</b>
                <div style={{ fontSize: 12, opacity: 0.65 }}>
                  {[r.tiktokId && `@${r.tiktokId.replace(/^@/, '')}`, r.customerPhone]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              </div>
            ),
          },
          {
            title: 'Tổng',
            dataIndex: 'totalAmount',
            align: 'right',
            width: 140,
            render: (v) => formatVnd(v),
          },
          {
            title: 'Công ty hỗ trợ',
            dataIndex: 'companyAmount',
            align: 'right',
            width: 140,
            render: (v) => formatVnd(v),
          },
          {
            title: 'Creator trả',
            dataIndex: 'creatorAmount',
            align: 'right',
            width: 130,
            render: (v) => formatVnd(v),
          },
          {
            title: 'Trạng thái',
            dataIndex: 'status',
            width: 140,
            render: (v) => (
              <Tag color={STATUS_COLOR[v as keyof typeof STATUS_COLOR]}>
                {STATUS_LABEL[v as keyof typeof STATUS_LABEL]}
              </Tag>
            ),
          },
          {
            title: 'Bàn giao',
            width: 150,
            render: (_, r) =>
              r.handoverAt ? (
                <Space direction="vertical" size={2}>
                  <Tag color="green">Đã bàn giao {dateText(r.handoverAt)}</Tag>
                  {r.paperReceived ? (
                    <Tag color="blue">Đã nộp bản giấy</Tag>
                  ) : (
                    <Tag color="orange">Chưa nộp bản giấy</Tag>
                  )}
                </Space>
              ) : (
                <span style={{ opacity: 0.45 }}>Chưa</span>
              ),
          },
          { title: 'Người lập', dataIndex: 'createdByName', width: 130 },
          { title: 'Cập nhật', dataIndex: 'updatedAt', width: 100, render: dateText },
          {
            width: 120,
            render: (_, r) => (
              <Space size={0}>
                <Button
                  type="text"
                  aria-label="Sửa"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/equipment-quotes/${r.id}`)}
                />
                <Button
                  type="text"
                  aria-label="Nhân bản"
                  icon={<CopyOutlined />}
                  onClick={() => duplicate(r.id)}
                />
                <Popconfirm
                  title="Xoá báo giá này?"
                  okText="Xoá"
                  cancelText="Huỷ"
                  onConfirm={() => remove(r.id)}
                >
                  <Button type="text" danger aria-label="Xoá" icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
    </div>
  );
};

// ---------- Tab Danh mục thiết bị ----------

const CatalogTab = () => {
  const [items, setItems] = useState<EquipmentItem[] | null>(null);
  const [editing, setEditing] = useState<Partial<EquipmentItem> | null>(null);
  const [form] = Form.useForm();

  const load = useCallback(() => {
    equipmentApi
      .items(true)
      .then(setItems)
      .catch((e) => {
        setItems([]);
        notification.error({ message: 'Không tải được danh mục', description: errorText(e) });
      });
  }, []);
  useEffect(load, [load]);

  const categories = useMemo(
    () => Array.from(new Set((items ?? []).map((i) => i.category))),
    [items]
  );
  const brands = useMemo(
    () => Array.from(new Set((items ?? []).map((i) => i.brand).filter(Boolean))) as string[],
    [items]
  );

  const open = (it: Partial<EquipmentItem>) => {
    setEditing(it);
    form.setFieldsValue({
      unit: 'cái',
      isActive: true,
      includeMode: 'NONE',
      sortOrder: 0,
      ...it,
      // Ô tải ảnh dùng { fileKey, fileUrl } (components/customized-upload); server chỉ lưu key.
      image: it.imageKey ? { fileKey: it.imageKey, fileUrl: imageUrl(it.imageKey) } : undefined,
    });
  };

  const submit = async () => {
    const { image, ...rest } = await form.validateFields();
    const values = { ...rest, imageKey: image?.fileKey || null };
    try {
      if (editing?.id) await equipmentApi.updateItem(editing.id, values);
      else await equipmentApi.createItem(values);
      setEditing(null);
      load();
    } catch (e) {
      notification.error({ message: 'Chưa lưu được', description: errorText(e) });
    }
  };

  return (
    <div>
      <div className={styles.toolbar}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => open({})}>
          Thêm thiết bị
        </Button>
        <span style={{ alignSelf: 'center', opacity: 0.7 }}>
          Đổi giá ở đây không làm đổi báo giá cũ — mỗi báo giá giữ giá lúc lập.
        </span>
      </div>
      <Table
        rowKey="id"
        loading={!items}
        dataSource={items ?? []}
        pagination={false}
        scroll={{ x: 800 }}
        columns={[
          {
            title: 'Nhóm',
            dataIndex: 'category',
            width: 190,
            filters: categories.map((c) => ({ text: c, value: c })),
            onFilter: (v, r) => r.category === v,
          },
          {
            title: 'Ảnh',
            dataIndex: 'imageKey',
            width: 72,
            render: (k: string | null) =>
              k ? (
                <img src={imageUrl(k)} alt="" className={styles.thumb} />
              ) : (
                <span className={styles.thumbEmpty}>—</span>
              ),
          },
          { title: 'Tên thiết bị', dataIndex: 'name' },
          {
            title: 'Hãng',
            dataIndex: 'brand',
            width: 120,
            filters: brands.map((b) => ({ text: b, value: b })),
            onFilter: (v, r) => r.brand === v,
            render: (v) => v || <span style={{ opacity: 0.4 }}>—</span>,
          },
          {
            title: 'Giá',
            dataIndex: 'price',
            align: 'right',
            width: 140,
            render: (v, r) =>
              r.unit === 'tháng' || r.unit === 'năm' ? `${formatVnd(v)}/${r.unit}` : formatVnd(v),
          },
          {
            title: 'Mọi gói',
            dataIndex: 'includeMode',
            width: 120,
            render: (v) =>
              v === 'FREE' ? (
                <Tag color="magenta">Tặng kèm</Tag>
              ) : v === 'ADVANCE' ? (
                <Tag color="orange">Ứng trước</Tag>
              ) : (
                ''
              ),
          },
          {
            title: 'Đang bán',
            dataIndex: 'isActive',
            width: 100,
            render: (v) => (v ? <Tag color="green">Có</Tag> : <Tag>Ẩn</Tag>),
          },
          { title: 'Thứ tự', dataIndex: 'sortOrder', width: 80 },
          {
            width: 96,
            render: (_, r) => (
              <Space size={0}>
                <Button
                  type="text"
                  aria-label="Sửa"
                  icon={<EditOutlined />}
                  onClick={() => open(r)}
                />
                <Popconfirm
                  title="Xoá thiết bị khỏi danh mục?"
                  description="Báo giá cũ vẫn giữ nguyên. Muốn tạm ngừng thì Sửa → tắt Đang bán."
                  okText="Xoá"
                  cancelText="Huỷ"
                  onConfirm={() =>
                    equipmentApi
                      .removeItem(r.id)
                      .then(load)
                      .catch((e) =>
                        notification.error({ message: 'Chưa xoá được', description: errorText(e) })
                      )
                  }
                >
                  <Button type="text" danger aria-label="Xoá" icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
      <Modal
        open={!!editing}
        title={editing?.id ? 'Sửa thiết bị' : 'Thêm thiết bị'}
        okText="Lưu"
        cancelText="Huỷ"
        onOk={submit}
        onCancel={() => setEditing(null)}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="category"
            label="Nhóm"
            rules={[{ required: true, message: 'Chọn hoặc gõ nhóm' }]}
          >
            <AutoComplete
              placeholder="Chọn nhóm có sẵn hoặc gõ nhóm mới"
              options={categories.map((c) => ({ value: c }))}
              filterOption={(input, opt) =>
                String(opt?.value ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item
            name="name"
            label="Tên thiết bị"
            rules={[{ required: true, message: 'Nhập tên' }]}
          >
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item name="brand" label="Hãng">
            <AutoComplete
              allowClear
              placeholder="VD: Sony, Focusrite, Logitech… (để trống nếu không rõ)"
              options={brands.map((b) => ({ value: b }))}
              filterOption={(input, opt) =>
                String(opt?.value ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="price"
                label="Giá (đ)"
                rules={[{ required: true, message: 'Nhập giá' }]}
              >
                <InputNumber<number>
                  min={0}
                  step={10000}
                  style={{ width: '100%' }}
                  formatter={(n) => `${n ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                  parser={(s) => Number((s || '').replace(/\D/g, '')) || 0}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="unit" label="Đơn vị">
                <Input maxLength={20} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="sortOrder" label="Thứ tự">
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="image"
            label="Ảnh sản phẩm (PNG / JPG, tối đa 5 MB — nên ảnh vuông, nền trắng)"
          >
            <CustomizedUpload type={UPLOAD_TYPE.IMAGE} maxFileSize={5} />
          </Form.Item>
          <Form.Item name="note" label="Ghi chú">
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item
            name="isActive"
            label="Đang bán (hiện khi lập báo giá)"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item name="includeMode" label="Có sẵn trong mọi gói (báo giá mới tự thêm)">
            <Select
              options={[
                { value: 'NONE', label: 'Không — món chọn thêm' },
                { value: 'FREE', label: 'Tặng kèm — Creator HP Media miễn phí' },
                { value: 'ADVANCE', label: 'Ứng trước — Công ty ứng tiền, Creator trả lại' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// ---------- Tab Cài đặt (Admin) ----------

const COMPANY_FIELDS: [keyof CompanyInfo, string, number][] = [
  ['name', 'Tên công ty (đúng giấy phép kinh doanh)', 24],
  ['shortName', 'Tên ngắn (in trên ảnh báo giá)', 8],
  ['taxCode', 'Mã số thuế', 8],
  ['phone', 'Điện thoại', 8],
  ['address', 'Địa chỉ trụ sở', 24],
  ['representative', 'Người đại diện ký hợp đồng', 8],
  ['position', 'Chức vụ', 8],
  ['email', 'Email', 8],
  ['bankAccount', 'Số tài khoản', 12],
  ['bankName', 'Ngân hàng, chi nhánh', 12],
  ['officeAddress', 'Văn phòng làm việc (in ở phần liên hệ)', 24],
  ['fanpage', 'Fanpage', 12],
  ['tiktok', 'TikTok', 12],
];

const SettingsTab = () => {
  const [s, setS] = useState<QuoteSettings | null>(null);
  const [perms, setPerms] = useState<PermissionRow[] | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    equipmentApi
      .settings()
      .then(setS)
      .catch((e) => notification.error({ message: errorText(e) }));
    equipmentApi
      .permissions()
      .then(setPerms)
      .catch(() => setPerms([]));
  }, []);

  if (!s) return <Spin />;
  const setCompany = (k: keyof CompanyInfo, v: string) =>
    setS({ ...s, company: { ...s.company, [k]: v } });

  const save = async () => {
    setSaving(true);
    try {
      setS(await equipmentApi.saveSettings(s));
      notification.success({ message: 'Đã lưu cài đặt' });
    } catch (e) {
      notification.error({ message: 'Chưa lưu được', description: errorText(e) });
    } finally {
      setSaving(false);
    }
  };

  const toggle = (row: PermissionRow, granted: boolean) =>
    equipmentApi
      .setPermission(row.id, granted)
      .then(() =>
        setPerms((list) => (list ?? []).map((p) => (p.id === row.id ? { ...p, granted } : p)))
      )
      .catch((e) => notification.error({ message: 'Chưa đổi được', description: errorText(e) }));

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card size="small" title="Thông tin Công ty (Bên A trong hợp đồng)">
        <Row gutter={[12, 10]}>
          {COMPANY_FIELDS.map(([k, label, span]) => (
            <Col xs={24} md={span} key={k}>
              <div className={styles.fieldLabel}>{label}</div>
              <Input value={s.company[k]} onChange={(e) => setCompany(k, e.target.value)} />
            </Col>
          ))}
        </Row>
      </Card>
      <Card size="small" title="Mặc định cho báo giá mới">
        <Row gutter={[16, 10]}>
          {(
            [
              ['payment', 'supportPercent', '% Công ty hỗ trợ', '%', 100],
              ['contract', 'commitMonths', 'Cho mượn tối đa', 'tháng', 12],
              ['contract', 'agencyTermYears', 'Gắn Đại lý TikTok', 'năm', 10],
              [
                'contract',
                'compensateNewPercent',
                'Mất / hỏng không BH: đền thiết bị mới',
                '%',
                100,
              ],
              [
                'contract',
                'compensateUsedPercent',
                'Mất / hỏng không BH: đền thiết bị cũ',
                '%',
                100,
              ],
              ['contract', 'incomeSharePercent', 'Trích thu nhập trả dần (0 = để trống)', '%', 100],
              ['contract', 'inactivityMonths', 'Không hoạt động → hoàn trả', 'tháng', 12],
              ['contract', 'minLiveDaysPerMonth', 'Tối thiểu ngày LIVE / tháng', 'ngày', 31],
              ['contract', 'minLiveHoursPerMonth', 'Tối thiểu giờ LIVE / tháng', 'giờ', 744],
              ['contract', 'unreachableDays', 'Không liên lạc được → vi phạm', 'ngày', 60],
              ['contract', 'returnDays', 'Hạn trả lại sau thông báo', 'ngày', 60],
              ['contract', 'penaltyPercent', 'Phạt vi phạm', '%', 100],
              ['contract', 'lateInterestPercentYear', 'Lãi chậm trả', '%/năm', 20],
            ] as const
          ).map(([group, key, label, suffix, max]) => (
            <Col xs={24} sm={12} md={8} key={key}>
              <div className={styles.fieldLabel}>{label}</div>
              <InputNumber
                min={0}
                max={max}
                addonAfter={suffix}
                value={(s[group] as any)[key]}
                onChange={(n) => setS({ ...s, [group]: { ...s[group], [key]: Number(n) || 0 } })}
              />
            </Col>
          ))}
          <Col xs={24}>
            <div className={styles.fieldLabel}>Lưu ý in cuối báo giá</div>
            <Input.TextArea
              rows={3}
              value={s.notes}
              onChange={(e) => setS({ ...s, notes: e.target.value })}
            />
          </Col>
        </Row>
      </Card>
      <Button type="primary" loading={saving} onClick={save} style={{ alignSelf: 'flex-start' }}>
        Lưu cài đặt
      </Button>

      <Card size="small" title="Người được dùng (MOD)">
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Admin luôn dùng được. KTV đang bật ở Hỗ trợ LIVE tự có quyền. MOD khác cần bật ở đây."
        />
        <Table
          rowKey="id"
          size="small"
          loading={!perms}
          dataSource={perms ?? []}
          pagination={{ defaultPageSize: 20 }}
          columns={[
            { title: 'Tên', render: (_, r) => r.nickName || r.username || r.email },
            { title: 'Email', dataIndex: 'email' },
            {
              title: 'KTV',
              dataIndex: 'isTechnician',
              width: 80,
              render: (v) => (v ? <Tag color="blue">KTV</Tag> : ''),
            },
            {
              title: 'Được dùng',
              width: 110,
              render: (_, r) => (
                <Switch
                  checked={r.granted || r.isTechnician}
                  disabled={r.isTechnician && !r.granted}
                  onChange={(v) => toggle(r, v)}
                />
              ),
            },
          ]}
        />
      </Card>
    </Space>
  );
};

const EquipmentQuotePage = () => {
  const { loaded, isAdmin, canUseEquipmentQuote } = useCurrentRoles();
  if (!loaded) return <Spin />;
  if (!canUseEquipmentQuote) return <NoAccess />;
  return (
    <TabbedPage
      tabs={[
        { key: 'quotes', label: 'Báo giá', render: () => <QuotesTab /> },
        { key: 'catalog', label: 'Danh mục thiết bị', render: () => <CatalogTab /> },
        { key: 'settings', label: 'Cài đặt', hidden: !isAdmin, render: () => <SettingsTab /> },
      ]}
    />
  );
};

export default EquipmentQuotePage;
